import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ConfermanceGiasActions } from './confermance-gias.actions';
import { ConfermanceGiasService } from '../../service/confermance-gias.service';
import { ManageCampaignConfermanceGiasActions } from '../manage-campaign-confermance-gias/manage-campaign-confermance-gias.actions';


@Injectable()
export class ConfermanceGiasEffects {

  loadConfirmanceGias$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(

        ofType(ConfermanceGiasActions.loadConfirmanceGias),
        mergeMap(({ campaignId, parameters }) =>
          rs.getConfirmanceGias(campaignId, parameters).pipe(
            map((confirmanceGias) => {
              return ConfermanceGiasActions.loadConfirmanceGiasSuccess({
                confirmanceGias
              });
            }),
            catchError((error) => of(ConfermanceGiasActions.loadConfirmanceGiasFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateConfermanceGias$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(
        ofType(ConfermanceGiasActions.updateConfirmanceGias),
        mergeMap(({ campaignId, parameters, input }) =>
          rs.updateConformanceGias(campaignId, input).pipe(
            mergeMap((update) => [
              // ConfermanceGiasActions.loadConfirmanceGias({ campaignId, parameters }),
              ConfermanceGiasActions.updateConfirmanceGiasSuccess({ update }),
              ConfermanceGiasActions.loadAllConfirmanceGias({ campaignId, parameters })
            ]),
            catchError((error) =>
              of(ConfermanceGiasActions.updateConfirmanceGiasFailure({ error, id: input.id as string }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  loadAllConfirmanceGias$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(

        ofType(ConfermanceGiasActions.loadAllConfirmanceGias),
        mergeMap(({ campaignId, parameters }) =>
          rs.getConfirmanceGias(campaignId, parameters).pipe(
            map((allConfirmanceGias) => {
              return ConfermanceGiasActions.loadAllConfirmanceGiasSuccess({
                allConfirmanceGias
              });
            }),
            catchError((error) => of(ConfermanceGiasActions.loadAllConfirmanceGiasFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getCampaign$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(

        ofType(ConfermanceGiasActions.getCampaigns),
        mergeMap(() =>
          rs.getConfirmancGiasCampaigns().pipe(
            map((campaigns) => {
              return ConfermanceGiasActions.getCampaignsSuccess({
                campaigns
              });
            }),
            catchError((error) => of(ConfermanceGiasActions.getCampaignsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  exportGias$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(
        ofType(ConfermanceGiasActions.exportGias),
        mergeMap(({ campaignId, timeZone, parameters }) =>
          rs.exportGias(campaignId, parameters, timeZone).pipe(
            map((file) => {
              // Download diretto del file (se presente)
              if (file?.data && file.data.length > 0) {
                const f = file.data[0];
                const byteChars = atob(f!.content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                  byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const link = window.document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = f!.filename ?? "gias.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              }
              return ConfermanceGiasActions.exportGiasSuccess({
                export: file
              });
            }),
            catchError((error) => of(ConfermanceGiasActions.exportGiasFailure({ error })))
          )
        )
      )
    }
  );
  createOrUpdateCampaigns$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(
        ofType(ConfermanceGiasActions.createOrUpdateCampaign),
        concatMap(({ campaignId, year, legalEntityTrackingIds, parameters }) =>
          rs.createOrUpdateCampaigns(campaignId, year as number, legalEntityTrackingIds).pipe(
            mergeMap((createOrUpdate) => {
              const actualCampaignId = createOrUpdate?.id || campaignId;
              return [
                ConfermanceGiasActions.createOrUpdateCampaignSuccess({
                  createOrUpdate
                }),
                ConfermanceGiasActions.loadAllConfirmanceGias({ campaignId: actualCampaignId, parameters }),
                ManageCampaignConfermanceGiasActions.loadLegalEntities({ campaignId: actualCampaignId, parameters })
              ];
            }),
            catchError((error) =>
              of(ConfermanceGiasActions.createOrUpdateCampaignFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  deleteCampaigns$ = createEffect(
    (rs: ConfermanceGiasService = inject(ConfermanceGiasService)) => {
      return this.actions$.pipe(

        ofType(ConfermanceGiasActions.closeCampaign),
        mergeMap(({ campaignId }) =>
          rs.closeCampaign(campaignId).pipe(
            map((closeCampaign) => {
              return ConfermanceGiasActions.closeCampaignSuccess({
                closeCampaign
              });
            }),
            catchError((error) => of(ConfermanceGiasActions.closeCampaignFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  closeCampaignSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ConfermanceGiasActions.closeCampaignSuccess),
        mergeMap(({ closeCampaign }) => {
          const campaignId = closeCampaign?.id;
          const defaultParameters = {
            page: 0,
            order: {
              sortField: null,
              sortDirection: "ASC"
            },
            search: null,
            filters: {},
            rows: 100000
          };

          const manageCampaignParameters = {
            campaignId: campaignId as string,
            page: {
              pageNo: 0,
              pageSize: 100000
            }
          };

          return [
            ConfermanceGiasActions.getCampaigns(), // Aggiorna la lista delle campagne per vedere il nuovo status
            ConfermanceGiasActions.loadAllConfirmanceGias({
              campaignId: campaignId as string,
              parameters: defaultParameters
            }),
            ConfermanceGiasActions.loadConfirmanceGias({
              campaignId: campaignId as string,
              parameters: { ...defaultParameters, rows: 10 }
            }),
            ManageCampaignConfermanceGiasActions.loadLegalEntities({
              campaignId: campaignId as string,
              parameters: manageCampaignParameters
            })
          ];
        })
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
